import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, Download, Loader2, Lock, Save, X, Edit3, Filter,
  Clock, Wallet, HandCoins, Gift, Coffee, Users,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '@/utils/api';

const fmtCLP = (n) => {
  if (!n) return '$0';
  return '$' + Math.round(Number(n)).toLocaleString('es-CL');
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; }
};

// Últimos 12 meses + 1 futuro
const buildPeriodos = () => {
  const out = [];
  const now = new Date();
  for (let i = -1; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const p = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
    out.push({ value: p, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return out;
};

export default function MatrizMaestraRRHH() {
  const periodos = useMemo(() => buildPeriodos(), []);
  const [periodo, setPeriodo] = useState(periodos[1].value); // mes actual
  const [items, setItems] = useState([]);
  const [catalogos, setCatalogos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [areaFiltro, setAreaFiltro] = useState('todas');
  const [drawerEmail, setDrawerEmail] = useState(null);
  const [auth403, setAuth403] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        api.get('/controles-rrhh/matriz', { params: { periodo } }),
        api.get('/controles-rrhh/catalogos').catch(() => ({ data: null })),
      ]);
      setItems(m.data.items || []);
      setCatalogos(c.data);
      setAuth403(false);
    } catch (err) {
      if (err?.response?.status === 403) setAuth403(true);
      else toast.error(formatErrorMessage(err));
    } finally { setLoading(false); }
  }, [periodo]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    return items.filter(it => {
      if (areaFiltro !== 'todas' && it.area !== areaFiltro) return false;
      if (!q) return true;
      return (it.name || '').toLowerCase().includes(q) ||
        (it.rut || '').toLowerCase().includes(q) ||
        (it.cargo || '').toLowerCase().includes(q);
    });
  }, [items, search, areaFiltro]);

  const totales = useMemo(() => filtered.reduce((acc, it) => {
    acc.anticipo += it.anticipo_clp || 0;
    acc.bonificacion += it.bonificacion_liquida_clp || 0;
    acc.aguinaldo += it.aguinaldo_clp || 0;
    acc.cuota_prestamo += it.cuota_prestamo_clp || 0;
    acc.horas_extras += it.horas_extras || 0;
    return acc;
  }, { anticipo: 0, bonificacion: 0, aguinaldo: 0, cuota_prestamo: 0, horas_extras: 0 }), [filtered]);

  const exportar = async () => {
    try {
      const r = await api.get('/controles-rrhh/export-novedades.xlsx', {
        params: { periodo }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `novedades_rrhh_${periodo}.xlsx`; a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('Excel descargado');
    } catch (err) { toast.error(formatErrorMessage(err)); }
  };

  if (auth403) {
    return (
      <Card className="p-12 text-center border-rose-200 bg-rose-50" data-testid="matriz-acceso-denegado">
        <Lock className="w-12 h-12 text-rose-400 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-rose-900">Acceso restringido</h3>
        <p className="text-sm text-rose-700 mt-2">Solo el equipo de RRHH puede ver la matriz.</p>
      </Card>
    );
  }

  const drawerItem = drawerEmail ? items.find(i => i.email === drawerEmail) : null;

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-xs text-slate-500">Período:</Label>
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="h-8 w-44" data-testid="matriz-periodo"><SelectValue /></SelectTrigger>
            <SelectContent>
              {periodos.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar nombre, RUT, cargo…"
              className="h-8 pl-7 w-60 text-xs"
              data-testid="matriz-search"
            />
          </div>
          <Select value={areaFiltro} onValueChange={setAreaFiltro}>
            <SelectTrigger className="h-8 w-44" data-testid="matriz-area"><Filter className="w-3 h-3 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas las áreas</SelectItem>
              {(catalogos?.areas || []).map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-slate-100 text-slate-700">{filtered.length} funcionario(s)</Badge>
          <Button onClick={exportar} className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid="matriz-export-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Exportar Novedades (Excel)
          </Button>
        </div>
      </div>

      {/* KPIs del mes */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        <Card className="p-2.5 border-amber-200 bg-amber-50">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-700" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-amber-700">Horas Extras</div>
              <div className="text-sm font-bold text-amber-900 tabular-nums truncate">{totales.horas_extras.toFixed(1)} h</div>
            </div>
          </div>
        </Card>
        <Card className="p-2.5 border-indigo-200 bg-indigo-50">
          <div className="flex items-center gap-2">
            <HandCoins className="w-4 h-4 text-indigo-700" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-indigo-700">Anticipos</div>
              <div className="text-sm font-bold text-indigo-900 tabular-nums truncate">{fmtCLP(totales.anticipo)}</div>
            </div>
          </div>
        </Card>
        <Card className="p-2.5 border-rose-200 bg-rose-50">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-rose-700" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-rose-700">Cuotas Préstamo</div>
              <div className="text-sm font-bold text-rose-900 tabular-nums truncate">{fmtCLP(totales.cuota_prestamo)}</div>
            </div>
          </div>
        </Card>
        <Card className="p-2.5 border-violet-200 bg-violet-50">
          <div className="flex items-center gap-2">
            <Gift className="w-4 h-4 text-violet-700" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-violet-700">Bonificaciones</div>
              <div className="text-sm font-bold text-violet-900 tabular-nums truncate">{fmtCLP(totales.bonificacion)}</div>
            </div>
          </div>
        </Card>
        <Card className="p-2.5 border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-2">
            <Coffee className="w-4 h-4 text-emerald-700" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-emerald-700">Aguinaldos</div>
              <div className="text-sm font-bold text-emerald-900 tabular-nums truncate">{fmtCLP(totales.aguinaldo)}</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Grilla */}
      <Card className="border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500"><Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />Sin funcionarios para esos filtros.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left px-2 py-1.5 sticky left-0 bg-slate-100">Funcionario</th>
                  <th className="text-left px-2 py-1.5 whitespace-nowrap">Área / Cargo</th>
                  <th className="text-left px-2 py-1.5 whitespace-nowrap">Contrato</th>
                  <th className="text-center px-2 py-1.5">Vac.</th>
                  <th className="text-center px-2 py-1.5">Admin.</th>
                  <th className="text-center px-2 py-1.5">H. Extras</th>
                  <th className="text-center px-2 py-1.5">Lic.</th>
                  <th className="text-center px-2 py-1.5">Inasist.</th>
                  <th className="text-right px-2 py-1.5">Anticipo</th>
                  <th className="text-right px-2 py-1.5">Cuota Prést.</th>
                  <th className="text-right px-2 py-1.5">Bonif. Líq.</th>
                  <th className="text-right px-2 py-1.5">Aguinaldo</th>
                  <th className="px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(it => (
                  <FilaFuncionario
                    key={it.email}
                    it={it}
                    periodo={periodo}
                    onSaved={load}
                    onOpenDrawer={() => setDrawerEmail(it.email)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {drawerItem && (
        <FichaDrawer
          item={drawerItem}
          periodo={periodo}
          catalogos={catalogos}
          onClose={() => setDrawerEmail(null)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}


// ============================================================
// FILA con inline edit en novedades del mes
// ============================================================
function FilaFuncionario({ it, periodo, onSaved, onOpenDrawer }) {
  const [editing, setEditing] = useState(null); // 'anticipo_clp' | 'aguinaldo_clp' | ...
  const [val, setVal] = useState('');
  const [saving, setSaving] = useState(false);

  const start = (field) => {
    setEditing(field);
    setVal(String(it[field] || ''));
  };

  const saveField = async (field) => {
    const num = Number(String(val).replace(/[^\d.]/g, '')) || 0;
    setSaving(true);
    try {
      await api.patch(`/controles-rrhh/novedades/${encodeURIComponent(it.email)}`, { [field]: num }, {
        params: { periodo },
      });
      toast.success('Guardado');
      setEditing(null);
      onSaved();
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally { setSaving(false); }
  };

  const cellEditable = (field, format = 'num', testid) => (
    <td className={`px-2 py-1.5 ${format === 'clp' ? 'text-right' : 'text-center'} tabular-nums`} data-testid={testid}>
      {editing === field ? (
        <span className="inline-flex items-center gap-1">
          <Input
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && saveField(field)}
            className="h-6 w-20 text-[11px] text-right"
            autoFocus
            disabled={saving}
          />
          <button onClick={() => saveField(field)} className="text-emerald-600 hover:text-emerald-700" disabled={saving} data-testid={`save-${field}-${it.email}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          </button>
          <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700" disabled={saving}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ) : (
        <button
          onClick={() => start(field)}
          className="hover:bg-amber-100 px-1.5 py-0.5 rounded cursor-pointer"
          title="Click para editar"
        >
          {format === 'clp' ? fmtCLP(it[field]) : (it[field] || 0)}
        </button>
      )}
    </td>
  );

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-2 py-1.5 sticky left-0 bg-white hover:bg-slate-50">
        <div className="font-semibold text-slate-800">{it.name}</div>
        <div className="text-[10px] text-slate-400">{it.rut || '—'}</div>
      </td>
      <td className="px-2 py-1.5">
        <div className="text-slate-700">{it.area || '—'}</div>
        <div className="text-[10px] text-slate-500">{it.cargo || '—'}</div>
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        <Badge className={`text-[9px] px-1.5 py-0 h-4 ${it.tipo_contrato === 'Plazo Fijo' ? 'bg-amber-100 text-amber-700' : it.tipo_contrato === 'Por Obra' ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}`}>{it.tipo_contrato}</Badge>
        <div className="text-[10px] text-slate-500 mt-0.5">{fmtDate(it.fecha_contrato)}</div>
      </td>
      <td className="px-2 py-1.5 text-center">
        <span className={`font-semibold ${it.saldo_vacaciones < 5 ? 'text-rose-600' : 'text-slate-700'}`}>{it.saldo_vacaciones?.toFixed(1)}d</span>
      </td>
      <td className="px-2 py-1.5 text-center">
        <span className={`font-semibold ${it.saldo_dias_admin <= 0 ? 'text-rose-600' : 'text-slate-700'}`}>{it.saldo_dias_admin}d</span>
      </td>
      {cellEditable('horas_extras', 'num', `cell-he-${it.email}`)}
      {cellEditable('dias_licencia', 'num', `cell-lic-${it.email}`)}
      {cellEditable('dias_inasistencia', 'num', `cell-inas-${it.email}`)}
      {cellEditable('anticipo_clp', 'clp', `cell-ant-${it.email}`)}
      {cellEditable('cuota_prestamo_clp', 'clp', `cell-pre-${it.email}`)}
      {cellEditable('bonificacion_liquida_clp', 'clp', `cell-bon-${it.email}`)}
      {cellEditable('aguinaldo_clp', 'clp', `cell-agu-${it.email}`)}
      <td className="px-2 py-1.5 text-right">
        <button onClick={onOpenDrawer} className="p-1 text-slate-400 hover:text-indigo-600" title="Editar ficha completa" data-testid={`drawer-open-${it.email}`}>
          <Edit3 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
}


// ============================================================
// DRAWER LATERAL: Editar Ficha completa + Novedades
// ============================================================
function FichaDrawer({ item, periodo, catalogos, onClose, onSaved }) {
  const [tab, setTab] = useState('ficha');
  const [f, setF] = useState({
    fecha_contrato: item.fecha_contrato || '',
    tipo_contrato: item.tipo_contrato || 'Indefinido',
    cargo: item.cargo || '',
    fecha_nacimiento: item.fecha_nacimiento || '',
    afp: item.afp || '',
    salud: item.salud || '',
    cargas_familiares: item.cargas_familiares || 0,
    herramientas: item.herramientas || '',
    dias_admin_anuales: item.dias_admin_anuales || 6,
    dias_admin_usados_anio: item.dias_admin_usados || 0,
    dias_vacaciones_usados: item.vacaciones_usadas || 0,
  });
  const [n, setN] = useState({
    horas_extras: item.horas_extras || 0,
    dias_licencia: item.dias_licencia || 0,
    dias_inasistencia: item.dias_inasistencia || 0,
    anticipo_clp: item.anticipo_clp || 0,
    cuota_prestamo_clp: item.cuota_prestamo_clp || 0,
    bonificacion_liquida_clp: item.bonificacion_liquida_clp || 0,
    aguinaldo_clp: item.aguinaldo_clp || 0,
    nota: item.nota_mes || '',
  });
  const [saving, setSaving] = useState(false);

  const saveFicha = async () => {
    setSaving(true);
    try {
      await api.patch(`/controles-rrhh/ficha/${encodeURIComponent(item.email)}`, f);
      toast.success('Ficha guardada');
      onSaved();
    } catch (err) { toast.error(formatErrorMessage(err)); }
    finally { setSaving(false); }
  };
  const saveNovedades = async () => {
    setSaving(true);
    try {
      await api.patch(`/controles-rrhh/novedades/${encodeURIComponent(item.email)}`, n, {
        params: { periodo },
      });
      toast.success('Novedades guardadas');
      onSaved();
    } catch (err) { toast.error(formatErrorMessage(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="matriz-drawer">
        <DialogHeader>
          <DialogTitle>{item.name}</DialogTitle>
          <p className="text-xs text-slate-500">{item.email} · {item.rut || '—'}</p>
        </DialogHeader>

        <div className="flex border-b border-slate-200 mt-3 mb-3">
          <button onClick={() => setTab('ficha')} className={`flex-1 py-2 text-sm border-b-2 ${tab === 'ficha' ? 'border-indigo-600 text-indigo-700 font-semibold' : 'border-transparent text-slate-500'}`} data-testid="drawer-tab-ficha">
            Ficha (Datos Atributivos)
          </button>
          <button onClick={() => setTab('novedades')} className={`flex-1 py-2 text-sm border-b-2 ${tab === 'novedades' ? 'border-indigo-600 text-indigo-700 font-semibold' : 'border-transparent text-slate-500'}`} data-testid="drawer-tab-novedades">
            Novedades del mes
          </button>
        </div>

        {tab === 'ficha' ? (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Fecha de Contrato</Label><Input type="date" value={f.fecha_contrato?.slice(0, 10) || ''} onChange={(e) => setF({ ...f, fecha_contrato: e.target.value })} data-testid="drawer-fecha-contrato" /></div>
              <div>
                <Label>Tipo de Contrato</Label>
                <Select value={f.tipo_contrato} onValueChange={(v) => setF({ ...f, tipo_contrato: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(catalogos?.tipos_contrato || ['Indefinido', 'Plazo Fijo', 'Por Obra']).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Cargo</Label><Input value={f.cargo} onChange={(e) => setF({ ...f, cargo: e.target.value })} /></div>
              <div><Label>Fecha de Cumpleaños</Label><Input type="date" value={f.fecha_nacimiento?.slice(0, 10) || ''} onChange={(e) => setF({ ...f, fecha_nacimiento: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>AFP</Label>
                <Select value={f.afp || '__none__'} onValueChange={(v) => setF({ ...f, afp: v === '__none__' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Sin asignar —</SelectItem>
                    {(catalogos?.afp || []).map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Salud</Label>
                <Select value={f.salud || '__none__'} onValueChange={(v) => setF({ ...f, salud: v === '__none__' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Sin asignar —</SelectItem>
                    {(catalogos?.salud || []).map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Cargas Familiares</Label><Input type="number" min="0" value={f.cargas_familiares} onChange={(e) => setF({ ...f, cargas_familiares: parseInt(e.target.value) || 0 })} /></div>
              <div><Label>Días Admin. Anuales</Label><Input type="number" min="0" value={f.dias_admin_anuales} onChange={(e) => setF({ ...f, dias_admin_anuales: parseInt(e.target.value) || 0 })} /></div>
              <div><Label>Días Admin. Usados (año)</Label><Input type="number" min="0" value={f.dias_admin_usados_anio} onChange={(e) => setF({ ...f, dias_admin_usados_anio: parseInt(e.target.value) || 0 })} /></div>
            </div>
            <div><Label>Días Vacaciones Usados</Label><Input type="number" min="0" step="0.5" value={f.dias_vacaciones_usados} onChange={(e) => setF({ ...f, dias_vacaciones_usados: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Herramientas Asignadas (glosa)</Label><Input value={f.herramientas} placeholder="MacBook Pro 14' ID-098, Monitor Dell ID-043" onChange={(e) => setF({ ...f, herramientas: e.target.value })} /></div>
            <div className="text-[11px] text-slate-500 bg-slate-50 p-2 rounded">
              Saldo vacaciones calculado: <strong>{item.saldo_vacaciones?.toFixed(1)} días</strong> ({item.vacaciones_acumuladas?.toFixed(1)} acumuladas - {item.vacaciones_usadas} usadas) · Saldo admin: <strong>{item.saldo_dias_admin} días</strong>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 p-2 rounded">
              Período evaluado: <strong>{periodo}</strong>. Los valores se reinician al cambiar el mes en el selector.
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Horas Extras</Label><Input type="number" step="0.5" min="0" value={n.horas_extras} onChange={(e) => setN({ ...n, horas_extras: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Días Licencia</Label><Input type="number" step="0.5" min="0" value={n.dias_licencia} onChange={(e) => setN({ ...n, dias_licencia: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Días Inasistencia</Label><Input type="number" step="0.5" min="0" value={n.dias_inasistencia} onChange={(e) => setN({ ...n, dias_inasistencia: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Anticipo (CLP)</Label><Input type="number" min="0" value={n.anticipo_clp} onChange={(e) => setN({ ...n, anticipo_clp: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Cuota Préstamo (CLP)</Label><Input type="number" min="0" value={n.cuota_prestamo_clp} onChange={(e) => setN({ ...n, cuota_prestamo_clp: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Bonificación Líquida (CLP)</Label><Input type="number" min="0" value={n.bonificacion_liquida_clp} onChange={(e) => setN({ ...n, bonificacion_liquida_clp: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Aguinaldo (CLP)</Label><Input type="number" min="0" value={n.aguinaldo_clp} onChange={(e) => setN({ ...n, aguinaldo_clp: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div><Label>Nota del mes</Label><Input value={n.nota} placeholder="Comentarios para liquidaciones…" onChange={(e) => setN({ ...n, nota: e.target.value })} /></div>
            <div className="text-[11px] text-slate-500 bg-slate-50 p-2 rounded">
              Documentos en repositorio para este mes — Licencias: <strong>{item.docs_licencias_mes || 0}</strong> · H. Extras (correos): <strong>{item.docs_horas_extras_mes || 0}</strong>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4 sm:flex-row sm:justify-between gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cerrar</Button>
          <Button onClick={tab === 'ficha' ? saveFicha : saveNovedades} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid={tab === 'ficha' ? 'drawer-save-ficha' : 'drawer-save-novedades'}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Guardar {tab === 'ficha' ? 'ficha' : 'novedades'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
